import { DragEvent, FC, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import CaresPageHeader from '../shell/cares-page-header';
import { getCaresShellBootstrap } from '../shell/bootstrap';
import {
  fetchMyScheduleOverviewLayout,
  listCalendarEvents,
  resetMyScheduleOverviewLayout,
  saveMyScheduleOverviewLayout,
  type CalendarEventRecord,
} from './api';
import {
  fetchMyScheduleRequests,
  type WorkflowRequestRecord,
  type WorkflowRequestType,
  type WorkflowSummary,
} from '../request-workflow/api';

type OverviewWidgetId =
  | 'upcoming-shifts'
  | 'pending-requests'
  | 'request-summary'
  | 'schedule-alerts'
  | 'shift-tallies'
  | 'coverage-counts'
  | 'comparison-summary';

type OverviewLayoutState = {
  order: OverviewWidgetId[];
  hidden: OverviewWidgetId[];
};

type OverviewWidgetTone = 'primary' | 'success' | 'warning' | 'info' | 'secondary';

type OverviewCalendarLayer = 'shifts' | 'admin' | 'approvedRequests' | 'tentativeRequests' | 'privateEvents' | 'other';

type OverviewCalendarColors = Record<OverviewCalendarLayer, string>;

type OverviewWidgetDefinition = {
  id: OverviewWidgetId;
  title: string;
  eyebrow: string;
  tone: OverviewWidgetTone;
  icon: string;
  summary: string;
  metricLabel: string;
  metricValue: string;
  detail: string;
  secondaryDetail?: string;
  upcomingEvents?: CalendarEventRecord[];
  cta?: {
    label: string;
    path: string;
  };
};

type OverviewDataState = {
  events: CalendarEventRecord[];
  requests: WorkflowRequestRecord[];
  requestSummary: WorkflowSummary;
};

const emptySummary: WorkflowSummary = {
  private: 0,
  pending: 0,
  approved: 0,
  denied: 0,
  cancelled: 0,
};

const overviewWidgetIds: OverviewWidgetId[] = [
  'upcoming-shifts',
  'pending-requests',
  'request-summary',
  'schedule-alerts',
  'shift-tallies',
  'coverage-counts',
  'comparison-summary',
];

const defaultLayoutState: OverviewLayoutState = {
  order: overviewWidgetIds,
  hidden: [],
};

const layoutStorageKey = 'cares.my-dashboard.overview-layout.v1';
const calendarColorStorageKey = 'cares-calendar-default-colors-v2';
const defaultCalendarColors: OverviewCalendarColors = {
  shifts: '#0d6efd',
  admin: '#6f42c1',
  approvedRequests: '#198754',
  tentativeRequests: '#ffc107',
  privateEvents: '#20c997',
  other: '#6c757d',
};

const isHexColor = (value?: string | null) => /^#[0-9a-f]{6}$/i.test(value ?? '');

const readCalendarColors = (): OverviewCalendarColors => {
  if (typeof window === 'undefined') return defaultCalendarColors;

  try {
    const saved = JSON.parse(window.localStorage.getItem(calendarColorStorageKey) ?? '{}') as Partial<OverviewCalendarColors>;

    return (Object.keys(defaultCalendarColors) as OverviewCalendarLayer[]).reduce<OverviewCalendarColors>(
      (colors, layer) => ({
        ...colors,
        [layer]: isHexColor(saved[layer]) ? saved[layer] as string : defaultCalendarColors[layer],
      }),
      { ...defaultCalendarColors }
    );
  } catch {
    return defaultCalendarColors;
  }
};

const parseLayoutState = (value: string | null): OverviewLayoutState => {
  if (!value) {
    return defaultLayoutState;
  }

  try {
    const parsed = JSON.parse(value) as Partial<OverviewLayoutState>;
    const knownIds = new Set(overviewWidgetIds);
    const order = Array.from(new Set((parsed.order ?? []).filter((entry): entry is OverviewWidgetId => knownIds.has(entry))));
    const hidden = Array.from(new Set((parsed.hidden ?? []).filter((entry): entry is OverviewWidgetId => knownIds.has(entry))));
    const missing = overviewWidgetIds.filter((entry) => !order.includes(entry));

    return {
      order: [...order, ...missing],
      hidden,
    };
  } catch {
    return defaultLayoutState;
  }
};

const parseServerLayoutState = (layout: { order?: unknown; hidden?: unknown } | null): OverviewLayoutState =>
  parseLayoutState(layout === null ? null : JSON.stringify(layout));

const moveWidget = (order: OverviewWidgetId[], widgetId: OverviewWidgetId, direction: -1 | 1) => {
  const currentIndex = order.indexOf(widgetId);
  const nextIndex = currentIndex + direction;

  if (currentIndex === -1 || nextIndex < 0 || nextIndex >= order.length) {
    return order;
  }

  const nextOrder = [...order];
  const [entry] = nextOrder.splice(currentIndex, 1);
  nextOrder.splice(nextIndex, 0, entry);

  return nextOrder;
};

const moveWidgetTo = (order: OverviewWidgetId[], source: OverviewWidgetId, target: OverviewWidgetId) => {
  const sourceIndex = order.indexOf(source);
  const targetIndex = order.indexOf(target);

  if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) {
    return order;
  }

  const nextOrder = [...order];
  const [entry] = nextOrder.splice(sourceIndex, 1);
  nextOrder.splice(targetIndex, 0, entry);

  return nextOrder;
};

const cloneLayoutState = (state: OverviewLayoutState): OverviewLayoutState => ({
  order: [...state.order],
  hidden: [...state.hidden],
});

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
  });

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const normalizeDateValue = (value: string) => value.includes('T') ? value.slice(0, 10) : value;

const toDateInput = (value: Date) => value.toISOString().slice(0, 10);

const dateRange = (start: string, end: string) => {
  const rangeStart = new Date(`${normalizeDateValue(start)}T00:00:00`);
  const rangeEnd = new Date(`${normalizeDateValue(end)}T00:00:00`);

  if (Number.isNaN(rangeStart.getTime()) || Number.isNaN(rangeEnd.getTime())) {
    return [];
  }

  const dates: string[] = [];
  const cursor = new Date(rangeStart <= rangeEnd ? rangeStart : rangeEnd);
  const finalDate = rangeStart <= rangeEnd ? rangeEnd : rangeStart;

  while (cursor <= finalDate) {
    dates.push(toDateInput(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
};

const requestTypeLabel: Record<WorkflowRequestType, string> = {
  pto: 'PTO',
  admin: 'Admin',
  academic: 'Academic',
  specific_shift: 'Request to work specific shifts',
  cannot_work_specific_shift: 'Cannot work specific shifts',
  swap_shift: 'Swap shifts',
  time_off: 'Time off',
  availability: 'Availability',
};

const latestRequestedDate = (request: WorkflowRequestRecord) => {
  const requestedDates = request.selectedDates.length > 0
    ? request.selectedDates.map(normalizeDateValue)
    : dateRange(request.start, request.end);

  return requestedDates.sort().at(-1) ?? normalizeDateValue(request.end);
};

const isActiveSubmittedRequest = (request: WorkflowRequestRecord, todayValue: string) =>
  ['pending', 'approved'].includes(request.workflowStatus) && latestRequestedDate(request) >= todayValue;

const isOverviewUpcomingEvent = (event: CalendarEventRecord) => {
  const workflow = event.metadata?.request_workflow;
  const workflowMetadata = workflow && typeof workflow === 'object'
    ? workflow as { kind?: string; request_type?: string; status?: string; visibility?: string }
    : null;
  const isRequestEvent = ['schedule_request', 'my_schedule_request'].includes(workflowMetadata?.kind ?? '');
  const isCancelled = event.status === 'cancelled' || workflowMetadata?.status === 'cancelled';
  const isPrivateEvent = event.source === 'manual'
    && ['meeting', 'reminder', 'task', 'other'].includes(event.type)
    && !isRequestEvent;
  const isIncludedRequest = isRequestEvent && workflowMetadata?.status === 'approved';
  const isShift = event.type === 'shift';
  const isAdmin = (workflowMetadata?.status === 'approved'
    && ['admin', 'academic'].includes(workflowMetadata.request_type ?? ''))
    || (!isRequestEvent && event.source !== 'manual')
    || ['time_off', 'availability'].includes(event.type);

  if (isCancelled) return false;
  if (isRequestEvent) return isIncludedRequest;

  return isShift || isAdmin || isPrivateEvent;
};

const resolveOverviewEventColor = (event: CalendarEventRecord, colors: OverviewCalendarColors) => {
  const workflow = event.metadata?.request_workflow;
  const request = workflow && typeof workflow === 'object'
    ? workflow as { kind?: string; request_type?: string; status?: string; visibility?: string }
    : null;
  const isRequest = ['schedule_request', 'my_schedule_request'].includes(request?.kind ?? '');
  const isPrivateEvent = event.source === 'manual'
    && ['meeting', 'reminder', 'task', 'other'].includes(event.type)
    && !isRequest;

  if (request && ['private', 'pending'].includes(request.status ?? '')) return colors.tentativeRequests;
  if (request?.status === 'approved' && ['admin', 'academic'].includes(request.request_type ?? '')) return colors.admin;
  if (request?.status === 'approved') return colors.approvedRequests;
  if (event.type === 'shift') return colors.shifts;
  if (isPrivateEvent) return isHexColor(event.color) ? event.color as string : colors.privateEvents;
  if (event.source !== 'manual' || ['time_off', 'availability'].includes(event.type)) return colors.admin;

  return colors.other;
};

const createOverviewWidgets = (
  data: OverviewDataState,
  authenticated: boolean
): OverviewWidgetDefinition[] => {
  const now = Date.now();
  const upcomingEvents = data.events
    .filter((event) => isOverviewUpcomingEvent(event) && new Date(event.end).getTime() >= now)
    .sort((left, right) => new Date(left.start).getTime() - new Date(right.start).getTime())
    .slice(0, 5);
  const nextUpcomingEvent = upcomingEvents[0] ?? null;
  const todayValue = toDateInput(new Date());
  const activeSubmittedRequests = data.requests.filter((request) => isActiveSubmittedRequest(request, todayValue));
  const hiddenCancelledPrivateRequests = data.requests.filter((request) => request.visibility === 'private' && request.workflowStatus === 'cancelled');
  const privateSubmittedRequests = data.requests.filter((request) => (request.workflowStatus === 'private' || request.visibility === 'private') && request.workflowStatus !== 'cancelled');
  const archivedSubmittedRequests = data.requests.filter(
    (request) => !isActiveSubmittedRequest(request, todayValue)
      && !privateSubmittedRequests.some((privateRequest) => privateRequest.id === request.id)
      && !hiddenCancelledPrivateRequests.some((privateRequest) => privateRequest.id === request.id)
  );
  const pendingActiveRequests = activeSubmittedRequests.filter((request) => request.workflowStatus === 'pending');
  const approvedActiveRequests = activeSubmittedRequests.filter((request) => request.workflowStatus === 'approved');
  const nextPendingRequest = pendingActiveRequests[0] ?? null;
  const approvedTimeOff = approvedActiveRequests.filter(
    (request) => ['pto', 'time_off'].includes(request.requestType)
  );
  const upcomingWeekends = upcomingEvents.filter((event) => {
    const day = new Date(event.start).getDay();
    return day === 0 || day === 6;
  });
  const manualEvents = upcomingEvents.filter((event) => event.source === 'manual');
  const allDayEvents = upcomingEvents.filter((event) => event.allDay);

  return [
    {
      id: 'upcoming-shifts',
      title: 'Upcoming Events',
      eyebrow: '',
      tone: 'primary',
      icon: 'ri-calendar-schedule-line',
      summary: nextUpcomingEvent
        ? `Next scheduled item: ${nextUpcomingEvent.title || 'Upcoming shift'}`
        : 'No future shifts are on your calendar yet.',
      metricLabel: 'Upcoming items',
      metricValue: String(upcomingEvents.length),
      detail: nextUpcomingEvent
        ? `${nextUpcomingEvent.allDay ? formatDate(nextUpcomingEvent.start) : formatDateTime(nextUpcomingEvent.start)}`
        : 'Connect or add schedule data to populate this card.',
      secondaryDetail: nextUpcomingEvent?.location ?? 'Read-only overview uses the current personal schedule feed.',
      upcomingEvents,
      cta: {
        label: 'My Schedule',
        path: '/my-schedule/calendar',
      },
    },
    {
      id: 'pending-requests',
      title: 'Pending requests',
      eyebrow: 'Workflow status',
      tone: 'warning',
      icon: 'ri-time-line',
      summary: pendingActiveRequests.length > 0
        ? `${pendingActiveRequests.length} future request${pendingActiveRequests.length === 1 ? '' : 's'} waiting on review.`
        : 'No future requests are waiting on review right now.',
      metricLabel: 'Future pending',
      metricValue: String(pendingActiveRequests.length),
      detail: nextPendingRequest
        ? `${requestTypeLabel[nextPendingRequest.requestType]} for ${nextPendingRequest.group.name}`
        : 'New pending requests will appear here after submission.',
      secondaryDetail: 'Only pending future submitted requests count here; archived requests stay below on My Requests.',
      cta: {
        label: 'My Requests',
        path: '/my-schedule/requests',
      },
    },
    {
      id: 'request-summary',
      title: 'PTO and request summary',
      eyebrow: 'Read-only counts',
      tone: 'success',
      icon: 'ri-briefcase-4-line',
      summary: authenticated
        ? `${activeSubmittedRequests.length} active submitted request${activeSubmittedRequests.length === 1 ? '' : 's'}, ${privateSubmittedRequests.length} private, and ${archivedSubmittedRequests.length} archived.`
        : 'Sign in to load your request counts.',
      metricLabel: 'Future approved PTO',
      metricValue: String(approvedTimeOff.length),
      detail: `${pendingActiveRequests.length} pending, ${approvedActiveRequests.length} approved, ${privateSubmittedRequests.length} private, ${archivedSubmittedRequests.length} archived`,
      secondaryDetail: 'Counts match My Requests: active pending/approved future requests, private requests, and prior archived requests.',
      cta: {
        label: 'Manage requests',
        path: '/my-schedule/requests',
      },
    },
    {
      id: 'schedule-alerts',
      title: 'Schedule review',
      eyebrow: 'Calendar and requests',
      tone: 'info',
      icon: 'ri-notification-3-line',
      summary: pendingActiveRequests.length > 0
        ? `Review ${pendingActiveRequests.length} pending request${pendingActiveRequests.length === 1 ? '' : 's'} and any recent calendar updates that could affect your availability.`
        : 'No pending requests need review right now. Use this card as a quick reminder to keep your schedule details current.',
      metricLabel: 'Items to review',
      metricValue: String(pendingActiveRequests.length + manualEvents.length),
      detail: manualEvents.length > 0
        ? `${manualEvents.length} manually added calendar item${manualEvents.length === 1 ? '' : 's'} may need a quick double-check for location, timing, or notes.`
        : 'Your upcoming calendar items currently come through without any manual schedule entries.',
      secondaryDetail: 'Check the calendar for shift details and My Requests for workflow updates when something changes.',
      cta: {
        label: 'Review schedule',
        path: '/my-schedule/calendar',
      },
    },
    {
      id: 'shift-tallies',
      title: 'Shift tallies',
      eyebrow: 'Derived from calendar',
      tone: 'secondary',
      icon: 'ri-bar-chart-box-line',
      summary: 'A quick count of the schedule items already visible to the end user.',
      metricLabel: 'Manual items',
      metricValue: String(manualEvents.length),
      detail: `${allDayEvents.length} all-day items across ${upcomingEvents.length} future schedule entries`,
      secondaryDetail: 'This avoids scheduler fairness logic and stays inside the user-visible event feed.',
      cta: {
        label: 'Review schedule',
        path: '/my-schedule/calendar',
      },
    },
    {
      id: 'coverage-counts',
      title: 'Weekend and holiday counts',
      eyebrow: 'Weekend snapshot',
      tone: 'primary',
      icon: 'ri-sun-line',
      summary: `${upcomingWeekends.length} future weekend assignment${upcomingWeekends.length === 1 ? '' : 's'} detected from your current calendar.`,
      metricLabel: 'Weekend shifts',
      metricValue: String(upcomingWeekends.length),
      detail: 'Holiday-specific tallies stay pending until the approved policy calendar feed is available.',
      secondaryDetail: 'This widget is safe to show because it only derives from your visible event dates.',
    },
    {
      id: 'comparison-summary',
      title: 'Department Tallies',
      eyebrow: 'Cross-team follow-up',
      tone: 'warning',
      icon: 'ri-scales-3-line',
      summary: 'When you need context beyond your own calendar, move into the shared schedule and tally workspaces from here.',
      metricLabel: 'Linked workspaces',
      metricValue: '2',
      detail: 'Use Department Schedule for shared coverage context and Department Tallies for broader workload views available to your role.',
      secondaryDetail: 'Overview stays focused on your personal schedule while shared analysis lives in its dedicated pages.',
      cta: {
        label: 'Open department tallies',
        path: '/my-schedule/group-tallies',
      },
    },
  ];
};

const MyScheduleHomePage: FC = () => {
  const bootstrap = getCaresShellBootstrap();
  const [layout, setLayout] = useState<OverviewLayoutState>(() =>
    parseLayoutState(typeof window === 'undefined' ? null : window.localStorage.getItem(layoutStorageKey))
  );
  const [draftLayout, setDraftLayout] = useState<OverviewLayoutState>(() =>
    parseLayoutState(typeof window === 'undefined' ? null : window.localStorage.getItem(layoutStorageKey))
  );
  const [draggingWidgetId, setDraggingWidgetId] = useState<OverviewWidgetId | null>(null);
  const [isEditingLayout, setIsEditingLayout] = useState(false);
  const [isSavingLayout, setIsSavingLayout] = useState(false);
  const [dashboardData, setDashboardData] = useState<OverviewDataState>({
    events: [],
    requests: [],
    requestSummary: emptySummary,
  });
  const [loading, setLoading] = useState(bootstrap.authenticated);
  const [error, setError] = useState<string | null>(null);
  const [layoutError, setLayoutError] = useState<string | null>(null);
  const [layoutLoading, setLayoutLoading] = useState(bootstrap.authenticated);
  const activeLayout = isEditingLayout ? draftLayout : layout;
  const [calendarColors, setCalendarColors] = useState<OverviewCalendarColors>(readCalendarColors);
  const widgets = createOverviewWidgets(dashboardData, bootstrap.authenticated);
  const widgetLookup = new Map(widgets.map((widget) => [widget.id, widget]));
  const orderedWidgets = activeLayout.order
    .map((widgetId) => widgetLookup.get(widgetId))
    .filter((widget): widget is OverviewWidgetDefinition => Boolean(widget));
  const visibleWidgets = orderedWidgets.filter((widget) => !activeLayout.hidden.includes(widget.id));

  const hydrateLayout = (nextLayout: OverviewLayoutState) => {
    const persisted = cloneLayoutState(nextLayout);
    setLayout(persisted);
    setDraftLayout(cloneLayoutState(persisted));
  };

  const setServerLayout = (nextLayout: OverviewLayoutState) => {
    hydrateLayout(nextLayout);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(layoutStorageKey);
    }
  };

  const beginLayoutEdit = () => {
    setDraftLayout(cloneLayoutState(layout));
    setIsEditingLayout(true);
  };

  const saveLayout = async () => {
    setIsSavingLayout(true);
    setLayoutError(null);

    try {
      const savedLayout = await saveMyScheduleOverviewLayout(draftLayout);
      setServerLayout(parseServerLayoutState(savedLayout));
      setIsEditingLayout(false);
    } catch (layoutRequestError) {
      setLayoutError(layoutRequestError instanceof Error ? layoutRequestError.message : 'Unable to save overview layout.');
    } finally {
      setIsSavingLayout(false);
    }
  };

  const cancelLayout = () => {
    setDraftLayout(cloneLayoutState(layout));
    setIsEditingLayout(false);
  };

  const resetLayout = async () => {
    setIsSavingLayout(true);
    setLayoutError(null);

    try {
      const resetLayoutState = await resetMyScheduleOverviewLayout();
      setServerLayout(parseServerLayoutState(resetLayoutState));
      setIsEditingLayout(false);
    } catch (layoutRequestError) {
      setLayoutError(layoutRequestError instanceof Error ? layoutRequestError.message : 'Unable to reset overview layout.');
    } finally {
      setIsSavingLayout(false);
    }
  };

  useEffect(() => {
    const refreshCalendarColors = () => setCalendarColors(readCalendarColors());

    window.addEventListener('focus', refreshCalendarColors);
    window.addEventListener('storage', refreshCalendarColors);

    return () => {
      window.removeEventListener('focus', refreshCalendarColors);
      window.removeEventListener('storage', refreshCalendarColors);
    };
  }, []);

  useEffect(() => {
    const storedLayout = parseLayoutState(typeof window === 'undefined' ? null : window.localStorage.getItem(layoutStorageKey));
    hydrateLayout(storedLayout);

    if (!bootstrap.authenticated) {
      setLayoutLoading(false);
      return;
    }

    const loadLayout = async () => {
      setLayoutLoading(true);
      setLayoutError(null);

      try {
        const persistedLayout = await fetchMyScheduleOverviewLayout();
        setServerLayout(parseServerLayoutState(persistedLayout));
      } catch (layoutRequestError) {
        setLayoutError(layoutRequestError instanceof Error ? layoutRequestError.message : 'Unable to load saved overview layout.');
      } finally {
        setLayoutLoading(false);
      }
    };

    void loadLayout();
  }, [bootstrap.authenticated]);

  useEffect(() => {
    if (!bootstrap.authenticated) {
      setLoading(false);
      return;
    }

    const loadDashboard = async () => {
      setLoading(true);
      setError(null);

      try {
        const [events, requestPayload] = await Promise.all([
          listCalendarEvents(),
          fetchMyScheduleRequests(),
        ]);

        setDashboardData({
          events,
          requests: requestPayload.requests,
          requestSummary: requestPayload.summary,
        });
      } catch (requestError) {
        setError(requestError instanceof Error ? requestError.message : 'Unable to load your schedule overview.');
        setDashboardData({
          events: [],
          requests: [],
          requestSummary: emptySummary,
        });
      } finally {
        setLoading(false);
      }
    };

    void loadDashboard();
  }, [bootstrap.authenticated]);

  useEffect(() => {
    if (isEditingLayout) {
      setError(null);
    }
  }, [isEditingLayout]);

  const updateWidgetOrder = (widgetId: OverviewWidgetId, direction: -1 | 1) => {
    setDraftLayout((current) => ({
      ...current,
      order: moveWidget(current.order, widgetId, direction),
    }));
  };

  const toggleWidgetVisibility = (widgetId: OverviewWidgetId) => {
    setDraftLayout((current) => ({
      ...current,
      hidden: current.hidden.includes(widgetId)
        ? current.hidden.filter((entry) => entry !== widgetId)
        : [...current.hidden, widgetId],
    }));
  };

  const handleDragStart = (event: DragEvent<HTMLDivElement>, widgetId: OverviewWidgetId) => {
    if (!isEditingLayout) {
      return;
    }

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', widgetId);
    setDraggingWidgetId(widgetId);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (isEditingLayout) {
      event.preventDefault();
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>, targetWidgetId: OverviewWidgetId) => {
    event.preventDefault();
    if (!isEditingLayout) {
      return;
    }

    const sourceWidgetId = event.dataTransfer.getData('text/plain') as OverviewWidgetId;
    if (!sourceWidgetId || sourceWidgetId === targetWidgetId) {
      setDraggingWidgetId(null);
      return;
    }

    setDraftLayout((current) => ({
      ...current,
      order: moveWidgetTo(current.order, sourceWidgetId, targetWidgetId),
    }));
    setDraggingWidgetId(null);
  };

  const handleDragEnd = () => {
    setDraggingWidgetId(null);
  };

  return (
    <>
      <style>{`
        .cares-my-schedule-focus-target {
          transition: box-shadow 0.2s ease, outline-color 0.2s ease, background-color 0.2s ease;
        }

        .cares-my-schedule-focus-target:focus {
          outline: 0;
        }

        .cares-my-schedule-focus-target:focus-visible {
          outline: 2px solid rgba(var(--primary-rgb), 0.55);
          outline-offset: 2px;
          box-shadow: 0 0 0 0.3rem rgba(var(--primary-rgb), 0.18);
          border-radius: 0.5rem;
          text-decoration: none;
        }

        .cares-my-schedule-overview-widget-card {
          transition: transform 0.15s ease, box-shadow 0.2s ease;
        }

        .cares-my-schedule-widget-canvas {
          background: var(--default-background);
          border-radius: var(--bs-border-radius-lg);
          padding: 1.5rem;
        }

        .cares-my-schedule-overview-widget-card[draggable='true'] {
          cursor: grab;
        }

        .cares-my-schedule-overview-widget-card[draggable='true']:active {
          cursor: grabbing;
        }

        .cares-my-schedule-overview-widget-card.dragging {
          opacity: 0.85;
        }

        .cares-my-schedule-available-widget[draggable='true'] {
          cursor: grab;
        }

        .cares-my-schedule-available-widget[draggable='true']:active {
          cursor: grabbing;
        }

        .cares-my-schedule-available-widget.dragging {
          opacity: 0.7;
        }
      `}</style>
      <CaresPageHeader homepage="My Schedule" activepage="My Schedule" page="Overview" actions={<></>} />

      <Row>
        <Col xl={12}>
          <Card className="custom-card cares-page-hero">
            <Card.Body className="p-3 p-xl-4">
              <div className="d-flex flex-column flex-xl-row align-items-xl-center justify-content-between gap-3">
                <div>
                  <Badge bg="primary-transparent" className="mb-3">
                    CARES My Schedule
                  </Badge>
                  <h2 className="mb-2">Your schedule at a glance</h2>
                  <p className="text-muted mb-0 fs-15" style={{ maxWidth: 720 }}>
                    Review your upcoming schedule, keep request activity visible, and arrange the cards that matter most.
                    Your Overview layout is saved to your account.
                  </p>
                </div>
                <div className="d-grid gap-2 d-sm-flex">
                  {bootstrap.authenticated ? (
                    isEditingLayout ? (
                      <>
                        <Button variant="primary" className="btn-wave" onClick={saveLayout} disabled={isSavingLayout}>
                          Save Layout
                        </Button>
                        <Button variant="light" className="btn-wave" onClick={cancelLayout} disabled={isSavingLayout}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button variant="primary" className="btn-wave" onClick={beginLayoutEdit}>
                          Modify Layout
                        </Button>
                        <Button variant="light" className="btn-wave" onClick={resetLayout} disabled={isSavingLayout}>
                          Reset Default Layout
                        </Button>
                      </>
                    )
                  ) : (
                    <Button variant="outline-primary" className="btn-wave" as={Link} to="/login">
                      Sign in to customize
                    </Button>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {!bootstrap.authenticated ? (
        <Alert variant="danger" className="custom-card">
          This Overview workspace requires an authenticated CARES session.
          <Link to="/login" className="alert-link ms-1">
            Sign in
          </Link>
          to load your schedule widgets and save your layout.
        </Alert>
      ) : null}

      {error ? <Alert variant="warning">{error}</Alert> : null}
      {layoutError ? <Alert variant="warning">{layoutError}</Alert> : null}

      <Row className="g-4 cares-my-schedule-widget-canvas">
        <Col xl={isEditingLayout ? 9 : 12}>
          {loading || layoutLoading ? (
            <div className="d-flex align-items-center gap-2 text-muted">
              <Spinner animation="border" size="sm" />
              <span>{loading ? 'Loading calendar and request widgets...' : 'Loading layout...'}</span>
            </div>
          ) : (
            <Row className="g-4">
                  {visibleWidgets.map((widget) => (
                    <Col xxl={6} xl={6} key={widget.id}>
                      <Card
                        className={`custom-card h-100 shadow-none border cares-my-schedule-overview-widget-card${
                          draggingWidgetId === widget.id ? ' dragging' : ''
                        }`}
                        draggable={isEditingLayout}
                        onDragStart={(event) => handleDragStart(event, widget.id)}
                        onDragOver={handleDragOver}
                        onDrop={(event) => handleDrop(event, widget.id)}
                        onDragEnd={handleDragEnd}
                      >
                        <Card.Header className="d-flex flex-wrap align-items-start justify-content-between gap-3">
                          <div className="d-flex align-items-start gap-3">
                            <span className={`avatar avatar-md bg-${widget.tone}-transparent text-${widget.tone}`}>
                              <i className={`${widget.icon} fs-20`} />
                            </span>
                            <div>
                              {widget.upcomingEvents ? (
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                  <div className="card-title fs-20 lh-1 mb-0">{widget.title}</div>
                                  <Badge bg="primary-transparent" className="text-primary">
                                    {widget.upcomingEvents.length} upcoming
                                  </Badge>
                                </div>
                              ) : (
                                <>
                                  <div className="text-muted text-uppercase fs-11 fw-semibold mb-1">{widget.eyebrow}</div>
                                  <div className="card-title mb-1">{widget.title}</div>
                                  <p className="text-muted mb-0 fs-12">{widget.summary}</p>
                                </>
                              )}
                            </div>
                          </div>
                        </Card.Header>
                        <Card.Body className="d-flex flex-column gap-3">
                          {widget.upcomingEvents ? (
                            widget.upcomingEvents.length ? (
                              <div className="d-flex flex-column gap-3">
                                {widget.upcomingEvents.map((event) => {
                                  const eventColor = resolveOverviewEventColor(event, calendarColors);

                                  return (
                                    <Link
                                      key={event.id}
                                      to="/my-schedule/calendar"
                                      className="btn text-start border w-100 cares-my-schedule-focus-target"
                                      style={{ borderLeft: `0.4rem solid ${eventColor}` }}
                                    >
                                      <div className="d-flex align-items-center gap-2 fw-semibold">
                                        <span
                                          className="rounded-circle flex-shrink-0"
                                          aria-hidden="true"
                                          style={{ width: '0.7rem', height: '0.7rem', backgroundColor: eventColor }}
                                        />
                                        <span style={{ color: eventColor }}>{event.title}</span>
                                      </div>
                                      <div className="text-muted fs-12">
                                        {event.allDay ? formatDate(event.start) : formatDateTime(event.start)}
                                        {event.location ? ` · ${event.location}` : ''}
                                      </div>
                                      <div className="d-flex flex-wrap gap-2 mt-2">
                                        <Badge bg="light" text="dark" className="border">{event.type}</Badge>
                                        <Badge bg="light" text="dark" className="border">{event.status}</Badge>
                                      </div>
                                    </Link>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-muted mb-0">No upcoming events yet.</p>
                            )
                          ) : (
                            <>
                              <div>
                                <p className="text-muted text-uppercase fs-12 fw-semibold mb-1">{widget.metricLabel}</p>
                                <h3 className="mb-1">{widget.metricValue}</h3>
                              </div>
                              <div className="border rounded p-3">
                                <p className="mb-1 fw-medium">{widget.detail}</p>
                                <p className="text-muted mb-0 fs-13">{widget.secondaryDetail}</p>
                              </div>
                              {widget.cta ? (
                                <div className="mt-auto">
                                  <Link to={widget.cta.path} className="fw-medium cares-my-schedule-focus-target">
                                    {widget.cta.label}
                                    <i className="ri-arrow-right-line align-middle ms-1" />
                                  </Link>
                                </div>
                              ) : null}
                            </>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
            </Row>
          )}
        </Col>
        {isEditingLayout ? (
          <Col xl={3}>
            <Card className="custom-card h-100">
              <Card.Header>
                <div className="card-title mb-1">Available widgets</div>
                <p className="text-muted mb-0 fs-12">Show, hide, or change the saved widget order.</p>
              </Card.Header>
              <Card.Body>
                <div className="d-flex flex-column gap-3">
                  {orderedWidgets.map((widget, index) => {
                    const isVisible = !activeLayout.hidden.includes(widget.id);

                    return (
                      <div
                        key={widget.id}
                        className={`border rounded p-2 bg-light cares-my-schedule-available-widget${
                          draggingWidgetId === widget.id ? ' dragging' : ''
                        }`}
                        draggable
                        onDragStart={(event) => handleDragStart(event, widget.id)}
                        onDragOver={handleDragOver}
                        onDrop={(event) => handleDrop(event, widget.id)}
                        onDragEnd={handleDragEnd}
                      >
                        <div className="d-flex align-items-center justify-content-between gap-2">
                          <div>
                            <div className="fw-semibold">{widget.title}</div>
                            <div className="text-muted fs-12">{isVisible ? 'Shown on Overview' : 'Hidden from Overview'}</div>
                          </div>
                          <div className="d-flex align-items-center gap-1 flex-shrink-0">
                            <Button
                              variant={isVisible ? 'outline-danger' : 'outline-success'}
                              size="sm"
                              className="cares-my-schedule-focus-target"
                              onClick={() => toggleWidgetVisibility(widget.id)}
                              aria-label={`${isVisible ? 'Hide' : 'Enable'} ${widget.title}`}
                            >
                              {isVisible ? 'Hide' : 'Enable'}
                            </Button>
                            <Button
                              variant="light"
                              size="sm"
                              className="border px-2 cares-my-schedule-focus-target"
                              disabled={index === 0}
                              onClick={() => updateWidgetOrder(widget.id, -1)}
                              aria-label={`Move ${widget.title} up`}
                            >
                              <i className="ri-arrow-up-line" aria-hidden="true" />
                            </Button>
                            <Button
                              variant="light"
                              size="sm"
                              className="border px-2 cares-my-schedule-focus-target"
                              disabled={index === orderedWidgets.length - 1}
                              onClick={() => updateWidgetOrder(widget.id, 1)}
                              aria-label={`Move ${widget.title} down`}
                            >
                              <i className="ri-arrow-down-line" aria-hidden="true" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ) : null}
      </Row>
    </>
  );
};

export default MyScheduleHomePage;
